import React, { useState, useEffect, useRef, useMemo } from "react";
import cn from "classnames";
import { Link, useNavigate } from "react-router-dom";
import styles from "./Header.module.sass";
import Icon from "../Icon";
import NotificationBadge from "../Messaging/NotificationBadge";
import NotificationsPanel from "../NotificationsPanel";
import { useChatContext } from "../../contexts/ChatContext";
import { aggressiveLogout, getAuthToken } from "../../utils/authHelpers";
import { authFetch } from "../../utils/authFetch";
import { getPropertyLabel } from "../../utils/labelHelpers";
import { useCurrentUserAvatar } from "../../hooks/useCurrentUserAvatar";
import { PERSONA_HOME, PERSONA_LABELS, usePersona } from "../../contexts/PersonaContext";

// Preferred Partner categories shown under the Partners nav item. Each links
// to /partners?category=<label>. Keep in sync with PARTNER_CATEGORIES in
// src/screens/Partners/index.js and ALLOWED_CATEGORIES in
// api/catalogue/partners.mjs.
const PARTNER_CATEGORIES = [
    'Marketing',
    'Design',
    'Legal',
    'Mentorship',
    'Real Estate',
    'Amenities',
    'Management',
    'Services',
];

// Full-color lockup on white headers; the white lockup (outlined stamp so the
// "P" stays legible) on the navy Learning Hub pages, which flag themselves by
// putting `learn-dark` on <body> (see src/screens/Learn/LearnShell.js).
const LOGO_SRC = "/images/postaway_full_logo.png";
const LOGO_SRC_DARK = "/images/postaway-logo-white.png";

const isLearnDark = () =>
    typeof document !== "undefined" && document.body.classList.contains("learn-dark");

const useLearnDark = () => {
    const [dark, setDark] = useState(isLearnDark);
    useEffect(() => {
        setDark(isLearnDark());
        const observer = new MutationObserver(() => setDark(isLearnDark()));
        observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
        return () => observer.disconnect();
    }, []);
    return dark;
};

const Header = ({ separatorHeader, wide, notAuthorized, teaser }) => {
    const learnDark = useLearnDark();
    const logoSrc = learnDark ? LOGO_SRC_DARK : LOGO_SRC;
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [collabDropdownOpen, setCollabDropdownOpen] = useState(false);
    const [mobileCollabExpanded, setMobileCollabExpanded] = useState(false);
    const [creatorsDropdownOpen, setCreatorsDropdownOpen] = useState(false);
    const [mobileCreatorsExpanded, setMobileCreatorsExpanded] = useState(false);
    const [hostsDropdownOpen, setHostsDropdownOpen] = useState(false);
    const [mobileHostsExpanded, setMobileHostsExpanded] = useState(false);
    const [travelDropdownOpen, setTravelDropdownOpen] = useState(false);
    const [mobileTravelExpanded, setMobileTravelExpanded] = useState(false);
    const [partnersDropdownOpen, setPartnersDropdownOpen] = useState(false);
    const [mobilePartnersExpanded, setMobilePartnersExpanded] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
    const [subscriptionChecked, setSubscriptionChecked] = useState(false);
    const [hostCategory, setHostCategory] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [unreadMessageCount, setUnreadMessageCount] = useState(0);
    const [avatarImgError, setAvatarImgError] = useState(false);
    const [getStartedOpen, setGetStartedOpen] = useState(false);
    const [preferredPartnerOpen, setPreferredPartnerOpen] = useState(false);
    const getStartedRef = useRef(null);
    const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
    const aboutDropdownRef = useRef(null);
    const [eventsDropdownOpen, setEventsDropdownOpen] = useState(false);
    const eventsDropdownRef = useRef(null);
    const [mobileEventsExpanded, setMobileEventsExpanded] = useState(false);
    const { disconnectChat } = useChatContext();
    const { roles, activePersona, switchPersona } = usePersona();
    const { avatarUrl } = useCurrentUserAvatar();
    const dropdownRef = useRef(null);
    const collabDropdownRef = useRef(null);
    const creatorsDropdownRef = useRef(null);
    const hostsDropdownRef = useRef(null);
    const travelDropdownRef = useRef(null);
    const partnersDropdownRef = useRef(null);
    const navigate = useNavigate();

    const navigationItems = [
        { label: "Creators", path: "/creators", active: true },
        { label: "Hosts", path: "/hosts", active: true },
        { label: "Collabs", path: "/collab-board", active: true },
        { label: "Travel", path: "/travel-board", active: true },
        { label: "Partners", path: "/partners", active: true },
        // Postaway Academy (the learning hub). Jamie asked for the noun
        // "Resources" to match Creators / Hosts / Collabs.
        { label: "Resources", path: "/learn", active: true },
        { label: "Events", path: "/hospitality-summit", active: true }
    ];

    // During rollout, PersonaContext is the source of truth and localStorage is the fallback.
    const userType = activePersona || (typeof window !== 'undefined' ? 
        window.localStorage.getItem('postaway_user_type') || '' : '');
    const isLoggedIn = typeof window !== 'undefined' ? !!window.localStorage.getItem('postaway_email') : false;
    const isTraveler = userType === 'traveler';
    const isPartner = userType === 'partner';
    
    const messagesBadge = unreadMessageCount > 0 ? unreadMessageCount : null;

    // Fetch unified unread count from notifications API
    const fetchUnreadCount = async () => {
        const token = getAuthToken();
        if (!token) {
            setUnreadCount(0);
            setUnreadMessageCount(0);
            return;
        }

        try {
            const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const response = await authFetch(`${apiBase}/api/notifications`, {}, { 
                disconnectChat,
                requireAuth: false // Don't logout if no token, just skip
            });

            if (response.ok) {
                const data = await response.json();
                if (data.ok) {
                    setUnreadCount(data.unreadCount || 0);

                    const msgCount = Array.isArray(data.notifications)
                        ? data.notifications.filter(n => n.type === 'message' && !n.isRead).length
                        : 0;
                    setUnreadMessageCount(msgCount);
                }
            }
        } catch (error) {
            if (!error.message?.includes('Unauthorized') && !error.message?.includes('expired')) {
                console.error('Error fetching unread count:', error);
            }
        }
    };

    // Fetch unread count on mount and periodically
    useEffect(() => {
        if (!isLoggedIn || isTraveler) {
            setUnreadCount(0);
            return;
        }
        // Partners use the messaging badge but don't need the full notification polling
        // (kept simple for now — fall through to the creator/host branch below).

        fetchUnreadCount();
        
        // Poll every 30 seconds to keep badge in sync
        const interval = setInterval(fetchUnreadCount, 30000);
        
        // Also refresh when window gains focus
        const handleFocus = () => {
            fetchUnreadCount();
        };
        window.addEventListener('focus', handleFocus);

        return () => {
            clearInterval(interval);
            window.removeEventListener('focus', handleFocus);
        };
    }, [isLoggedIn, isTraveler]);

    // Refresh count when notifications panel closes (in case user marked things as read)
    useEffect(() => {
        if (!notificationsOpen) {
            // Small delay to ensure API has processed any mark-read requests
            const timer = setTimeout(() => {
                fetchUnreadCount();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [notificationsOpen]);

    // Check subscription status on mount and when login status changes
    useEffect(() => {
        const checkSubscription = async () => {
            if (!isLoggedIn) {
                setHasActiveSubscription(false);
                setSubscriptionChecked(true);
                return;
            }

            try {
                const email = typeof window !== 'undefined' ? 
                    window.localStorage.getItem('postaway_email') : '';
                
                if (!email) {
                    setHasActiveSubscription(false);
                    setSubscriptionChecked(true);
                    return;
                }

                const res = await fetch('/api/access/subscription', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email })
                });
                
                const data = await res.json();
                
                if (res.ok && data.ok && data.active) {
                    setHasActiveSubscription(true);
                    
                    // Fetch host profile if user is a host
                    if (userType === 'host') {
                        try {
                            const apiBase = window.location.hostname === 'localhost' ? 
                                'http://localhost:4000' : '';
                            const profileResponse = await fetch(`${apiBase}/api/host/profile?email=${encodeURIComponent(email)}`);
                            const profileData = await profileResponse.json();
                            if (profileData.ok && profileData.profile) {
                                setHostCategory(profileData.profile.host_category);
                            }
                        } catch (profileErr) {
                            console.error('Failed to fetch host profile:', profileErr);
                        }
                    }
                } else {
                    setHasActiveSubscription(false);
                }
            } catch (err) {
                console.error('Error checking subscription:', err);
                setHasActiveSubscription(false);
            } finally {
                setSubscriptionChecked(true);
            }
        };

        checkSubscription();
    }, [isLoggedIn, userType]);

    // Only show navigation if user is logged in AND has active subscription
    const canSeeNav = isLoggedIn && hasActiveSubscription;

    // Adjust nav visibility based on role/login AND subscription
    const canSeeCollab = canSeeNav && (userType === 'creator' || userType === 'host');
    const canSeeTravel = canSeeNav;
    const isCreatorOrHost = userType === 'creator' || userType === 'host';
    
    const navItems = navigationItems.map((item) => {
        if (item.label === 'Collabs') return { ...item, active: canSeeCollab };
        if (item.label === 'Travel') return { ...item, active: canSeeTravel };
        // Only show other nav items if user has active subscription
        if (item.external) return { ...item, active: canSeeNav }; // Store link
        return { ...item, active: canSeeNav };
    });

    // Recalculate dropdown menu items when hostCategory changes
    const dropdownMenuItems = useMemo(() => {
        const propertiesLabel = getPropertyLabel(hostCategory, true);
        
        return userType === 'creator' ? [
            { title: "Dashboard", icon: "home", path: "/creator-dashboard" },
            { title: "Messages", icon: "comment", path: "/messages", badge: messagesBadge },
            { title: "Itineraries", icon: "document", path: "/itineraries" },
            { title: "Collaborations", icon: "thumbs-up", path: "/collab-board" },
            { title: "Get Featured", icon: "image", path: "/get-featured" },
            { title: "Reviews", icon: "star", path: "/creator-dashboard?tab=reviews" },
            { title: "Settings", icon: "settings", path: "/creator-dashboard?tab=settings" },
        ] : [
            { title: "Dashboard", icon: "home", path: "/host-dashboard" },
            { title: propertiesLabel, icon: "building", path: "/host-dashboard?tab=properties" },
            { title: "Messages", icon: "comment", path: "/messages", badge: messagesBadge },
            { title: "Reviews", icon: "star", path: "/host-dashboard?tab=reviews" },
            { title: "Settings", icon: "settings", path: "/host-dashboard?tab=settings" },
        ];
    }, [hostCategory, userType, messagesBadge]);

    const renderMenuIcon = (name) => {
        // Use lucide for specific creator dropdown icons
        if (name === 'document') {
            return (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ fill: 'none' }}>
                    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" fill="none" style={{ fill: 'none' }} />
                    <path d="M14 2v4a2 2 0 0 0 2 2h4" fill="none" style={{ fill: 'none' }} />
                    <path d="M10 9H8" fill="none" style={{ fill: 'none' }} />
                    <path d="M16 13H8" fill="none" style={{ fill: 'none' }} />
                    <path d="M16 17H8" fill="none" style={{ fill: 'none' }} />
                </svg>
            );
        }
        if (name === 'thumbs-up') {
            return (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ fill: 'none' }}>
                    <path d="M7 10v12" fill="none" style={{ fill: 'none' }} />
                    <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z" fill="none" style={{ fill: 'none' }} />
                </svg>
            );
        }
        if (name === 'settings') {
            return (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ fill: 'none' }}>
                    <path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915" fill="none" style={{ fill: 'none' }} />
                    <circle cx="12" cy="12" r="3" fill="none" style={{ fill: 'none' }} />
                </svg>
            );
        }
        if (name === 'image') {
            return (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ fill: 'none' }}>
                    <rect width="18" height="18" x="3" y="3" rx="2" ry="2" fill="none" style={{ fill: 'none' }} />
                    <circle cx="9" cy="9" r="2" fill="none" style={{ fill: 'none' }} />
                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" fill="none" style={{ fill: 'none' }} />
                </svg>
            );
        }
        return <Icon name={name} size="16" />;
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };

        if (dropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownOpen]);

    // Close collab dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (collabDropdownRef.current && !collabDropdownRef.current.contains(event.target)) {
                setCollabDropdownOpen(false);
            }
        };

        if (collabDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [collabDropdownOpen]);

    // Close creators dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (creatorsDropdownRef.current && !creatorsDropdownRef.current.contains(event.target)) {
                setCreatorsDropdownOpen(false);
            }
        };

        if (creatorsDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [creatorsDropdownOpen]);

    // Close hosts dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (hostsDropdownRef.current && !hostsDropdownRef.current.contains(event.target)) {
                setHostsDropdownOpen(false);
            }
        };

        if (hostsDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [hostsDropdownOpen]);

    // Close travel dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (travelDropdownRef.current && !travelDropdownRef.current.contains(event.target)) {
                setTravelDropdownOpen(false);
            }
        };

        if (travelDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [travelDropdownOpen]);

    // Close partners dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (partnersDropdownRef.current && !partnersDropdownRef.current.contains(event.target)) {
                setPartnersDropdownOpen(false);
            }
        };

        if (partnersDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [partnersDropdownOpen]);

    const handleMenuItemClick = (path) => {
        setDropdownOpen(false);
        navigate(path);
    };

    const handlePersonaClick = async (persona) => {
        // Close the dropdown immediately so the user doesn't see the menu
        // recompute its items mid-switch (which previously looked like the
        // menu was "flashing" between creator and host variants).
        setDropdownOpen(false);
        try {
            const nextPersona = await switchPersona(persona);
            navigate(PERSONA_HOME[nextPersona || persona] || '/dashboard');
        } catch (error) {
            console.error('Failed to switch persona:', error);
        }
    };

    // Defensively close the avatar dropdown whenever the active persona
    // changes (via switcher OR by external events like persona-changed). This
    // prevents a stale menu — built against the previous persona — from
    // sticking around after a switch.
    useEffect(() => {
        setDropdownOpen(false);
        setMobileMenuOpen(false);
    }, [activePersona]);

    const renderPersonaSwitcher = () => {
        if (!roles || roles.length <= 1) return null;
        return (
            <>
                {roles.map((role) => (
                    <button
                        key={role}
                        className={styles.dropdownItem}
                        onClick={() => handlePersonaClick(role)}
                        disabled={role === userType}
                    >
                        <Icon name="user" size="16" />
                        <span>{role === userType ? `Using ${PERSONA_LABELS[role] || role}` : `Switch to ${PERSONA_LABELS[role] || role}`}</span>
                    </button>
                ))}
                <div className={styles.dropdownDivider} />
            </>
        );
    };

    useEffect(() => {
        if (!getStartedOpen) {
            setPreferredPartnerOpen(false);
            return;
        }
        const handleClickOutside = (e) => {
            if (getStartedRef.current && !getStartedRef.current.contains(e.target)) {
                setGetStartedOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [getStartedOpen]);

    useEffect(() => {
        if (!aboutDropdownOpen) return;
        const handleClickOutside = (e) => {
            if (aboutDropdownRef.current && !aboutDropdownRef.current.contains(e.target)) {
                setAboutDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [aboutDropdownOpen]);

    useEffect(() => {
        if (!eventsDropdownOpen) return;
        const handleClickOutside = (e) => {
            if (eventsDropdownRef.current && !eventsDropdownRef.current.contains(e.target)) {
                setEventsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [eventsDropdownOpen]);

    // Don't render navigation if subscription check hasn't completed or user doesn't have active subscription
    if (!subscriptionChecked || !canSeeNav) {
        return (
            <div
                className={cn(
                    styles.header,
                    { [styles.headerBorder]: separatorHeader },
                    { [styles.wide]: wide }
                )}
            >
                <div className={cn("container", styles.container)}>
                    {/* Logo only - no navigation */}
                    <Link to="/" className={styles.logo}>
                        <img 
                            src={logoSrc}
                            alt="Postaway" 
                        />
                    </Link>
                    {!isLoggedIn && subscriptionChecked && (
                        <div className={styles.headerActions}>
                            <div className={cn(styles.navDropdown, styles.aboutDropdown)} ref={aboutDropdownRef}>
                                <button
                                    className={cn(styles.navItem, styles.navDropdownTrigger)}
                                    onClick={() => setAboutDropdownOpen(prev => !prev)}
                                >
                                    About Postaway
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="currentColor"
                                        className={cn(styles.navDropdownArrow, { [styles.open]: aboutDropdownOpen })}
                                    >
                                        <path d="M10.805 6.362c-.26-.26-.682-.26-.943 0L8 8.224 6.138 6.362c-.26-.26-.682-.26-.943 0s-.26.682 0 .943l2.333 2.333c.26.26.682.26.943 0l2.333-2.333c.26-.26.26-.682 0-.943z" />
                                    </svg>
                                </button>
                                {aboutDropdownOpen && (
                                    <div className={cn(styles.dropdownMenu, styles.navDropdownMenu)}>
                                        <Link to="/what-is-postaway" className={styles.dropdownItem} onClick={() => setAboutDropdownOpen(false)}>
                                            What is Postaway
                                        </Link>
                                        <Link to="/why-postaway" className={styles.dropdownItem} onClick={() => setAboutDropdownOpen(false)}>
                                            Why Postaway
                                        </Link>
                                        <Link to="/how-to-use-postaway" className={styles.dropdownItem} onClick={() => setAboutDropdownOpen(false)}>
                                            How to use Postaway
                                        </Link>
                                        <Link to="/our-story" className={styles.dropdownItem} onClick={() => setAboutDropdownOpen(false)}>
                                            Our Story
                                        </Link>
                                        <Link to="/contact" className={styles.dropdownItem} onClick={() => setAboutDropdownOpen(false)}>
                                            Contact
                                        </Link>
                                    </div>
                                )}
                            </div>
                            <div className={cn(styles.navDropdown, styles.aboutDropdown)} ref={eventsDropdownRef}>
                                <button
                                    className={cn(styles.navItem, styles.navDropdownTrigger)}
                                    onClick={() => setEventsDropdownOpen(prev => !prev)}
                                >
                                    Events
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="currentColor"
                                        className={cn(styles.navDropdownArrow, { [styles.open]: eventsDropdownOpen })}
                                    >
                                        <path d="M10.805 6.362c-.26-.26-.682-.26-.943 0L8 8.224 6.138 6.362c-.26-.26-.682-.26-.943 0s-.26.682 0 .943l2.333 2.333c.26.26.682.26.943 0l2.333-2.333c.26-.26.26-.682 0-.943z" />
                                    </svg>
                                </button>
                                {eventsDropdownOpen && (
                                    <div className={cn(styles.dropdownMenu, styles.navDropdownMenu)}>
                                        <Link to="/hospitality-summit-2027" className={styles.dropdownItem} onClick={() => setEventsDropdownOpen(false)}>
                                            Hospitality Summit 2027
                                        </Link>
                                        <Link to="/hospitality-summit" className={styles.dropdownItem} onClick={() => setEventsDropdownOpen(false)}>
                                            Hospitality Summit 2026
                                        </Link>
                                        <Link to="/inner-circle" className={styles.dropdownItem} onClick={() => setEventsDropdownOpen(false)}>
                                            Inner Circle Program
                                        </Link>
                                    </div>
                                )}
                            </div>
                            <div ref={getStartedRef} style={{ position: 'relative' }}>
                            <button
                                type="button"
                                className={styles.getStartedBtn}
                                onClick={() => setGetStartedOpen(prev => !prev)}
                            >
                                Get Started
                                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ marginLeft: 6 }}>
                                    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            {getStartedOpen && (
                                <div className={styles.getStartedDropdown}>
                                    <Link to="/creator-signup" className={styles.getStartedItem} onClick={() => setGetStartedOpen(false)}>
                                        Creator
                                    </Link>
                                    <Link to="/host-select" className={styles.getStartedItem} onClick={() => setGetStartedOpen(false)}>
                                        Host
                                    </Link>
                                    <Link to="/traveler-signup" className={styles.getStartedItem} onClick={() => setGetStartedOpen(false)}>
                                        Traveler
                                    </Link>
                                    <button
                                        type="button"
                                        className={cn(styles.getStartedItem, styles.getStartedSubTrigger)}
                                        aria-expanded={preferredPartnerOpen}
                                        onClick={() => setPreferredPartnerOpen(prev => !prev)}
                                    >
                                        Preferred Partner
                                        <svg
                                            width="10"
                                            height="6"
                                            viewBox="0 0 10 6"
                                            fill="none"
                                            className={cn(styles.getStartedSubArrow, { [styles.open]: preferredPartnerOpen })}
                                            aria-hidden="true"
                                        >
                                            <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>
                                    {preferredPartnerOpen && (
                                        <div className={styles.getStartedSubmenu}>
                                            <Link
                                                to="/partners"
                                                className={styles.getStartedSubItem}
                                                onClick={() => setGetStartedOpen(false)}
                                            >
                                                Preferred Partners
                                            </Link>
                                            <Link
                                                to="/partner-signup"
                                                className={styles.getStartedSubItem}
                                                onClick={() => setGetStartedOpen(false)}
                                            >
                                                Apply to be a Partner
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            )}
                            </div>
                        </div>
                    )}
                    {/* Logged in without a membership (e.g. a summit attendee or
                        Academy package buyer): still needs a way to the hub. */}
                    {isLoggedIn && subscriptionChecked && (
                        <div className={styles.headerActions}>
                            <Link to="/learn" className={styles.navItem}>
                                Resources
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <>
            <div
                className={cn(
                    styles.header,
                    { [styles.headerBorder]: separatorHeader },
                    { [styles.wide]: wide }
                )}
            >
                <div className={cn("container", styles.container)}>
                    {/* Logo */}
                    <Link to="/" className={styles.logo}>
                        <img 
                            src={logoSrc}
                            alt="Postaway" 
                        />
                    </Link>
                    
                    {/* Desktop Navigation - only shown for verified paying members */}
                    <nav className={styles.navigation}>
                        {navItems.filter((item) => item.active).map((item, index) => {
                            if (item.label === 'Creators') {
                                return (
                                    <div key={index} className={styles.navDropdown} ref={creatorsDropdownRef}>
                                        <button
                                            className={cn(styles.navItem, styles.navDropdownTrigger)}
                                            onClick={() => setCreatorsDropdownOpen(!creatorsDropdownOpen)}
                                        >
                                            Creators
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="currentColor"
                                                className={cn(styles.navDropdownArrow, { [styles.open]: creatorsDropdownOpen })}
                                            >
                                                <path d="M10.805 6.362c-.26-.26-.682-.26-.943 0L8 8.224 6.138 6.362c-.26-.26-.682-.26-.943 0s-.26.682 0 .943l2.333 2.333c.26.26.682.26.943 0l2.333-2.333c.26-.26.26-.682 0-.943z" />
                                            </svg>
                                        </button>
                                        {creatorsDropdownOpen && (
                                            <div className={cn(styles.dropdownMenu, styles.navDropdownMenu)}>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setCreatorsDropdownOpen(false);
                                                        navigate('/creators');
                                                    }}
                                                >
                                                    <span>Meet Our Creators</span>
                                                </button>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setCreatorsDropdownOpen(false);
                                                        navigate('/creators/storefronts');
                                                    }}
                                                >
                                                    <span>Storefronts</span>
                                                </button>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setCreatorsDropdownOpen(false);
                                                        navigate('/creators/spotlight');
                                                    }}
                                                >
                                                    <span>Creator Spotlight</span>
                                                    <span className={styles.newBadge}>New</span>
                                                </button>
                                                {isCreatorOrHost && (
                                                    <button
                                                        className={styles.dropdownItem}
                                                        onClick={() => {
                                                            setCreatorsDropdownOpen(false);
                                                            navigate('/creators/reviews');
                                                        }}
                                                    >
                                                        <span>Reviews</span>
                                                        <span className={styles.newBadge}>New</span>
                                                    </button>
                                                )}
                                                {isCreatorOrHost && (
                                                    <button
                                                        className={styles.dropdownItem}
                                                        onClick={() => {
                                                            setCreatorsDropdownOpen(false);
                                                            navigate('/creators/get-featured');
                                                        }}
                                                    >
                                                        <span>Get Featured</span>
                                                    </button>
                                                )}
                                                {isCreatorOrHost && (
                                                    <button
                                                        className={styles.dropdownItem}
                                                        onClick={() => {
                                                            setCreatorsDropdownOpen(false);
                                                            navigate('/get-verified');
                                                        }}
                                                    >
                                                        <span>Get Verified</span>
                                                        <span className={styles.newBadge}>New</span>
                                                    </button>
                                                )}
                                                <button
                                                    className={cn(styles.dropdownItem, styles.dropdownItemDisabled)}
                                                    disabled
                                                >
                                                    <span>Creator Resources</span>
                                                    <span className={styles.comingSoonBadge}>Coming Soon</span>
                                                </button>
                                                {isCreatorOrHost && (
                                                    <button
                                                        className={cn(styles.dropdownItem, styles.dropdownItemDisabled)}
                                                        disabled
                                                    >
                                                        <span>Refer a Creator</span>
                                                        <span className={styles.comingSoonBadge}>Coming Soon</span>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            if (item.label === 'Hosts') {
                                return (
                                    <div key={index} className={styles.navDropdown} ref={hostsDropdownRef}>
                                        <button
                                            className={cn(styles.navItem, styles.navDropdownTrigger)}
                                            onClick={() => setHostsDropdownOpen(!hostsDropdownOpen)}
                                        >
                                            Hosts
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="currentColor"
                                                className={cn(styles.navDropdownArrow, { [styles.open]: hostsDropdownOpen })}
                                            >
                                                <path d="M10.805 6.362c-.26-.26-.682-.26-.943 0L8 8.224 6.138 6.362c-.26-.26-.682-.26-.943 0s-.26.682 0 .943l2.333 2.333c.26.26.682.26.943 0l2.333-2.333c.26-.26.26-.682 0-.943z" />
                                            </svg>
                                        </button>
                                        {hostsDropdownOpen && (
                                            <div className={cn(styles.dropdownMenu, styles.navDropdownMenu)}>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setHostsDropdownOpen(false);
                                                        navigate('/hosts');
                                                    }}
                                                >
                                                    <span>Search Our Properties</span>
                                                </button>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setHostsDropdownOpen(false);
                                                        navigate('/hosts/storefronts');
                                                    }}
                                                >
                                                    <span>Shop Our Properties</span>
                                                </button>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setHostsDropdownOpen(false);
                                                        navigate('/hosts/spotlight');
                                                    }}
                                                >
                                                    <span>Host Spotlight</span>
                                                    <span className={styles.newBadge}>New</span>
                                                </button>
                                                {isCreatorOrHost && (
                                                    <button
                                                        className={styles.dropdownItem}
                                                        onClick={() => {
                                                            setHostsDropdownOpen(false);
                                                            navigate('/hosts/reviews');
                                                        }}
                                                    >
                                                        <span>Reviews</span>
                                                        <span className={styles.newBadge}>New</span>
                                                    </button>
                                                )}
                                                {isCreatorOrHost && (
                                                    <button
                                                        className={styles.dropdownItem}
                                                        onClick={() => {
                                                            setHostsDropdownOpen(false);
                                                            navigate('/hosts/get-featured');
                                                        }}
                                                    >
                                                        <span>Get Featured</span>
                                                    </button>
                                                )}
                                                {isCreatorOrHost && (
                                                    <button
                                                        className={styles.dropdownItem}
                                                        onClick={() => {
                                                            setHostsDropdownOpen(false);
                                                            navigate('/get-verified');
                                                        }}
                                                    >
                                                        <span>Get Verified</span>
                                                        <span className={styles.newBadge}>New</span>
                                                    </button>
                                                )}
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setHostsDropdownOpen(false);
                                                        navigate('/hosts/resources');
                                                    }}
                                                >
                                                    <span>Host Resources</span>
                                                    <span className={styles.newBadge}>New</span>
                                                </button>
                                                {isCreatorOrHost && (
                                                    <button
                                                        className={cn(styles.dropdownItem, styles.dropdownItemDisabled)}
                                                        disabled
                                                    >
                                                        <span>Refer a Host</span>
                                                        <span className={styles.comingSoonBadge}>Coming Soon</span>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            if (item.label === 'Collabs') {
                                return (
                                    <div key={index} className={styles.navDropdown} ref={collabDropdownRef}>
                                        <button
                                            className={cn(styles.navItem, styles.navDropdownTrigger)}
                                            onClick={() => setCollabDropdownOpen(!collabDropdownOpen)}
                                        >
                                            Collabs
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="currentColor"
                                                className={cn(styles.navDropdownArrow, { [styles.open]: collabDropdownOpen })}
                                            >
                                                <path d="M10.805 6.362c-.26-.26-.682-.26-.943 0L8 8.224 6.138 6.362c-.26-.26-.682-.26-.943 0s-.26.682 0 .943l2.333 2.333c.26.26.682.26.943 0l2.333-2.333c.26-.26.26-.682 0-.943z" />
                                            </svg>
                                        </button>
                                        {collabDropdownOpen && (
                                            <div className={cn(styles.dropdownMenu, styles.navDropdownMenu)}>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setCollabDropdownOpen(false);
                                                        navigate('/collab-board');
                                                    }}
                                                >
                                                    <span>Current Opportunities</span>
                                                </button>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setCollabDropdownOpen(false);
                                                        navigate('/collabs');
                                                    }}
                                                >
                                                    <span>My Collab Tracker</span>
                                                </button>
                                                <button
                                                    className={cn(styles.dropdownItem, styles.dropdownItemDisabled)}
                                                    disabled
                                                >
                                                    <span>Best Practices</span>
                                                    <span className={styles.comingSoonBadge}>Coming Soon</span>
                                                </button>
                                                <button
                                                    className={cn(styles.dropdownItem, styles.dropdownItemDisabled)}
                                                    disabled
                                                >
                                                    <span>FAQ</span>
                                                    <span className={styles.comingSoonBadge}>Coming Soon</span>
                                                </button>
                                                <button
                                                    className={cn(styles.dropdownItem, styles.dropdownItemDisabled)}
                                                    disabled
                                                >
                                                    <span>Postaway Collabs</span>
                                                    <span className={styles.comingSoonBadge}>Coming Soon</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            if (item.label === 'Travel') {
                                return (
                                    <div key={index} className={styles.navDropdown} ref={travelDropdownRef}>
                                        <button
                                            className={cn(styles.navItem, styles.navDropdownTrigger)}
                                            onClick={() => setTravelDropdownOpen(!travelDropdownOpen)}
                                        >
                                            Travel
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="currentColor"
                                                className={cn(styles.navDropdownArrow, { [styles.open]: travelDropdownOpen })}
                                            >
                                                <path d="M10.805 6.362c-.26-.26-.682-.26-.943 0L8 8.224 6.138 6.362c-.26-.26-.682-.26-.943 0s-.26.682 0 .943l2.333 2.333c.26.26.682.26.943 0l2.333-2.333c.26-.26.26-.682 0-.943z" />
                                            </svg>
                                        </button>
                                        {travelDropdownOpen && (
                                            <div className={cn(styles.dropdownMenu, styles.navDropdownMenu)}>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setTravelDropdownOpen(false);
                                                        navigate('/travel-board');
                                                    }}
                                                >
                                                    <span>All Opportunities</span>
                                                </button>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setTravelDropdownOpen(false);
                                                        navigate('/itineraries');
                                                    }}
                                                >
                                                    <span>Itineraries</span>
                                                </button>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setTravelDropdownOpen(false);
                                                        navigate('/travel-board?filter=group_trips');
                                                    }}
                                                >
                                                    <span>Group Trips</span>
                                                </button>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setTravelDropdownOpen(false);
                                                        navigate('/travel-board?filter=substack');
                                                    }}
                                                >
                                                    <span>Substack / Newsletter / Blog</span>
                                                </button>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setTravelDropdownOpen(false);
                                                        navigate('/travel-board?filter=podcast');
                                                    }}
                                                >
                                                    <span>Podcast</span>
                                                </button>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setTravelDropdownOpen(false);
                                                        navigate('/travel-board?filter=last_minute');
                                                    }}
                                                >
                                                    <span>Last Minute Availability</span>
                                                </button>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setTravelDropdownOpen(false);
                                                        navigate('/travel-board?filter=discount_codes');
                                                    }}
                                                >
                                                    <span>Discount Codes</span>
                                                </button>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setTravelDropdownOpen(false);
                                                        navigate('/travel-board?filter=upcoming_events');
                                                    }}
                                                >
                                                    <span>Upcoming Events</span>
                                                </button>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setTravelDropdownOpen(false);
                                                        navigate('/travel-board');
                                                    }}
                                                >
                                                    <span>General Announcement</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            if (item.label === 'Partners') {
                                return (
                                    <div key={index} className={styles.navDropdown} ref={partnersDropdownRef}>
                                        <button
                                            className={cn(styles.navItem, styles.navDropdownTrigger)}
                                            onClick={() => setPartnersDropdownOpen(!partnersDropdownOpen)}
                                        >
                                            Partners
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="currentColor"
                                                className={cn(styles.navDropdownArrow, { [styles.open]: partnersDropdownOpen })}
                                            >
                                                <path d="M10.805 6.362c-.26-.26-.682-.26-.943 0L8 8.224 6.138 6.362c-.26-.26-.682-.26-.943 0s-.26.682 0 .943l2.333 2.333c.26.26.682.26.943 0l2.333-2.333c.26-.26.26-.682 0-.943z" />
                                            </svg>
                                        </button>
                                        {partnersDropdownOpen && (
                                            <div className={cn(styles.dropdownMenu, styles.navDropdownMenu)}>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setPartnersDropdownOpen(false);
                                                        navigate('/partners');
                                                    }}
                                                >
                                                    <span>Preferred Partners</span>
                                                </button>
                                                {PARTNER_CATEGORIES.map((label) => (
                                                    <button
                                                        key={label}
                                                        className={styles.dropdownItem}
                                                        onClick={() => {
                                                            setPartnersDropdownOpen(false);
                                                            navigate(`/partners?category=${encodeURIComponent(label)}`);
                                                        }}
                                                    >
                                                        <span>{label}</span>
                                                    </button>
                                                ))}
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setPartnersDropdownOpen(false);
                                                        navigate('/partners');
                                                    }}
                                                >
                                                    <span>Apply to be a Partner</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            if (item.label === 'Events') {
                                return (
                                    <div key={index} className={styles.navDropdown} ref={eventsDropdownRef}>
                                        <button
                                            className={cn(styles.navItem, styles.navDropdownTrigger)}
                                            onClick={() => setEventsDropdownOpen(!eventsDropdownOpen)}
                                        >
                                            Events
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="currentColor"
                                                className={cn(styles.navDropdownArrow, { [styles.open]: eventsDropdownOpen })}
                                            >
                                                <path d="M10.805 6.362c-.26-.26-.682-.26-.943 0L8 8.224 6.138 6.362c-.26-.26-.682-.26-.943 0s-.26.682 0 .943l2.333 2.333c.26.26.682.26.943 0l2.333-2.333c.26-.26.26-.682 0-.943z" />
                                            </svg>
                                        </button>
                                        {eventsDropdownOpen && (
                                            <div className={cn(styles.dropdownMenu, styles.navDropdownMenu)}>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setEventsDropdownOpen(false);
                                                        navigate('/hospitality-summit-2027');
                                                    }}
                                                >
                                                    <span>Hospitality Summit 2027</span>
                                                </button>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setEventsDropdownOpen(false);
                                                        navigate('/hospitality-summit');
                                                    }}
                                                >
                                                    <span>Hospitality Summit 2026</span>
                                                </button>
                                                <button
                                                    className={styles.dropdownItem}
                                                    onClick={() => {
                                                        setEventsDropdownOpen(false);
                                                        navigate('/inner-circle');
                                                    }}
                                                >
                                                    <span>Inner Circle Program</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            return item.external ? (
                                <a
                                    key={index}
                                    href={item.path}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.navItem}
                                >
                                    {item.label}
                                </a>
                            ) : (
                                <Link
                                    key={index}
                                    to={item.path}
                                    className={styles.navItem}
                                >
                                    {item.label}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* Notification Bell (only when logged in, has subscription, and not a traveler or partner) */}
                    {canSeeNav && !isTraveler && !isPartner && (
                        <>
                            <button 
                                className={styles.notificationBtn}
                                onClick={() => setNotificationsOpen(true)}
                                title="Notifications"
                            >
                                <Icon name="bell" size="20" />
                                {unreadCount > 0 && (
                                    <NotificationBadge count={unreadCount} />
                                )}
                            </button>
                            <NotificationsPanel 
                                isOpen={notificationsOpen} 
                                onClose={() => {
                                    setNotificationsOpen(false);
                                    // Refresh count when panel closes
                                    setTimeout(() => fetchUnreadCount(), 500);
                                }}
                                onNotificationRead={() => {
                                    // Refresh count immediately when notification is marked as read
                                    fetchUnreadCount();
                                }}
                            />
                        </>
                    )}

                    {/* Partner Avatar with Dropdown */}
                    {canSeeNav && isPartner && (
                        <div className={styles.avatarDropdown} ref={dropdownRef}>
                            <button
                                className={styles.avatarButton}
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                            >
                                <div className={cn(styles.avatar, { [styles.active]: dropdownOpen })}>
                                    {avatarUrl && !avatarImgError ? (
                                        <img
                                            src={avatarUrl}
                                            alt="Business logo"
                                            className={styles.avatarImage}
                                            onError={() => setAvatarImgError(true)}
                                        />
                                    ) : (
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor" />
                                            <path d="M12 14C7.03125 14 3 18.0312 3 23H21C21 18.0312 16.9688 14 12 14Z" fill="currentColor" />
                                        </svg>
                                    )}
                                </div>
                            </button>
                            {dropdownOpen && (
                                <div className={styles.dropdownMenu}>
                                    {renderPersonaSwitcher()}
                                    <button
                                        className={styles.dropdownItem}
                                        onClick={() => handleMenuItemClick('/messages')}
                                    >
                                        <Icon name="message" size="16" />
                                        <span>Messages</span>
                                        {messagesBadge && messagesBadge > 0 && (
                                            <span className={styles.badgeInDropdown}>{messagesBadge}</span>
                                        )}
                                    </button>
                                    <button
                                        className={styles.dropdownItem}
                                        onClick={() => handleMenuItemClick('/partner-settings')}
                                    >
                                        {renderMenuIcon('settings')}
                                        <span>Partner Settings</span>
                                    </button>
                                    <button
                                        className={styles.dropdownItem}
                                        onClick={() => handleMenuItemClick('/plan-settings')}
                                    >
                                        {renderMenuIcon('settings')}
                                        <span>Plan & Add-ons</span>
                                    </button>
                                    <div className={styles.dropdownDivider} />
                                    <button
                                        className={cn(styles.dropdownItem, styles.dropdownLogout)}
                                        onClick={() => {
                                            setDropdownOpen(false);
                                            aggressiveLogout(disconnectChat || null);
                                        }}
                                    >
                                        <Icon name="exit" size="16" />
                                        <span>Logout</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Traveler Avatar with Dropdown */}
                    {canSeeNav && isTraveler && (
                        <div className={styles.avatarDropdown} ref={dropdownRef}>
                            <button 
                                className={styles.avatarButton}
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                            >
                                <div className={cn(styles.avatar, { [styles.active]: dropdownOpen })}>
                                    <svg 
                                        width="24" 
                                        height="24" 
                                        viewBox="0 0 24 24" 
                                        fill="none" 
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path 
                                            d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" 
                                            fill="currentColor"
                                        />
                                        <path 
                                            d="M12 14C7.03125 14 3 18.0312 3 23H21C21 18.0312 16.9688 14 12 14Z" 
                                            fill="currentColor"
                                        />
                                    </svg>
                                </div>
                            </button>

                            {dropdownOpen && (
                                <div className={styles.dropdownMenu}>
                                    {renderPersonaSwitcher()}
                                    <button
                                        className={styles.dropdownItem}
                                        onClick={() => handleMenuItemClick('/traveler-settings')}
                                    >
                                        {renderMenuIcon('settings')}
                                        <span>Settings</span>
                                    </button>
                                    <button
                                        className={styles.dropdownItem}
                                        onClick={() => handleMenuItemClick('/plan-settings')}
                                    >
                                        {renderMenuIcon('settings')}
                                        <span>Plan & Add-ons</span>
                                    </button>
                                    <div className={styles.dropdownDivider} />
                                    <button
                                        className={cn(styles.dropdownItem, styles.dropdownLogout)}
                                        onClick={() => {
                                            setDropdownOpen(false);
                                            aggressiveLogout(disconnectChat || null);
                                        }}
                                    >
                                        <Icon name="exit" size="16" />
                                        <span>Logout</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* User Avatar with Dropdown (creator / host) */}
                    {canSeeNav && !isTraveler && !isPartner && (
                        <div className={styles.avatarDropdown} ref={dropdownRef}>
                            <button 
                                className={styles.avatarButton}
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                            >
                                <div className={cn(styles.avatar, { [styles.active]: dropdownOpen })}>
                                    {avatarUrl && !avatarImgError ? (
                                        <img 
                                            src={avatarUrl} 
                                            alt="User avatar"
                                            className={styles.avatarImage}
                                            onError={() => setAvatarImgError(true)}
                                        />
                                    ) : (
                                        <svg 
                                            width="24" 
                                            height="24" 
                                            viewBox="0 0 24 24" 
                                            fill="none" 
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path 
                                                d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" 
                                                fill="currentColor"
                                            />
                                            <path 
                                                d="M12 14C7.03125 14 3 18.0312 3 23H21C21 18.0312 16.9688 14 12 14Z" 
                                                fill="currentColor"
                                            />
                                        </svg>
                                    )}
                                </div>
                            </button>

                            {/* Dropdown Menu */}
                            {dropdownOpen && (
                                <div className={styles.dropdownMenu}>
                                    {renderPersonaSwitcher()}
                                    {dropdownMenuItems.map((item, index) => (
                                        <button
                                            key={index}
                                            className={styles.dropdownItem}
                                            onClick={() => handleMenuItemClick(item.path)}
                                        >
                                            {renderMenuIcon(item.icon)}
                                            <span>{item.title}</span>
                                            {item.badge && item.badge > 0 && (
                                                <span className={styles.badgeInDropdown}>{item.badge}</span>
                                            )}
                                        </button>
                                    ))}
                                    <div className={styles.dropdownDivider} />
                                    <button
                                        className={styles.dropdownItem}
                                        onClick={() => handleMenuItemClick('/profile')}
                                    >
                                        <Icon name="user" size="16" />
                                        <span>Profile</span>
                                    </button>
                                    <button
                                        className={styles.dropdownItem}
                                        onClick={() => handleMenuItemClick('/plan-settings')}
                                    >
                                        {renderMenuIcon('settings')}
                                        <span>Plan & Add-ons</span>
                                    </button>
                                    <div className={styles.dropdownDivider} />
                                    <button
                                        className={cn(styles.dropdownItem, styles.dropdownLogout)}
                                        onClick={() => {
                                            setDropdownOpen(false);
                                            aggressiveLogout(disconnectChat || null);
                                        }}
                                    >
                                        <Icon name="exit" size="16" />
                                        <span>Logout</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Mobile Menu Burger */}
                    <button
                        className={cn(styles.burger, {
                            [styles.active]: mobileMenuOpen,
                        })}
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    />
                </div>
            </div>

            {/* Mobile Menu */}
            <div
                className={cn(styles.mobileMenu, {
                    [styles.active]: mobileMenuOpen,
                })}
            >
                <nav className={styles.mobileNav}>
                    {navItems.filter((item) => item.active).map((item, index) => {
                        if (item.label === 'Creators') {
                            return (
                                <div key={index}>
                                    <button
                                        className={styles.mobileNavItem}
                                        onClick={() => setMobileCreatorsExpanded(!mobileCreatorsExpanded)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            width: '100%',
                                            textAlign: 'left',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        Creators
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 16 16"
                                            fill="currentColor"
                                            style={{
                                                transition: 'transform 0.3s ease',
                                                transform: mobileCreatorsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            }}
                                        >
                                            <path d="M10.805 6.362c-.26-.26-.682-.26-.943 0L8 8.224 6.138 6.362c-.26-.26-.682-.26-.943 0s-.26.682 0 .943l2.333 2.333c.26.26.682.26.943 0l2.333-2.333c.26-.26.26-.682 0-.943z" />
                                        </svg>
                                    </button>
                                    {mobileCreatorsExpanded && (
                                        <>
                                            <Link
                                                to="/creators"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Meet Our Creators
                                            </Link>
                                            <Link
                                                to="/creators/storefronts"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Storefronts
                                            </Link>
                                            <Link
                                                to="/creators/spotlight"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem, styles.mobileSubItemWithBadge)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                <span>Creator Spotlight</span>
                                                <span className={styles.newBadge}>New</span>
                                            </Link>
                                            {isCreatorOrHost && (
                                                <Link
                                                    to="/creators/reviews"
                                                    className={cn(styles.mobileNavItem, styles.mobileSubItem, styles.mobileSubItemWithBadge)}
                                                    onClick={() => setMobileMenuOpen(false)}
                                                >
                                                    <span>Reviews</span>
                                                    <span className={styles.newBadge}>New</span>
                                                </Link>
                                            )}
                                            {isCreatorOrHost && (
                                                <Link
                                                    to="/creators/get-featured"
                                                    className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                    onClick={() => setMobileMenuOpen(false)}
                                                >
                                                    Get Featured
                                                </Link>
                                            )}
                                            {isCreatorOrHost && (
                                                <Link
                                                    to="/get-verified"
                                                    className={cn(styles.mobileNavItem, styles.mobileSubItem, styles.mobileSubItemWithBadge)}
                                                    onClick={() => setMobileMenuOpen(false)}
                                                >
                                                    <span>Get Verified</span>
                                                    <span className={styles.newBadge}>New</span>
                                                </Link>
                                            )}
                                            <span
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                style={{ opacity: 0.45, cursor: 'default' }}
                                            >
                                                Creator Resources (Coming Soon)
                                            </span>
                                            {isCreatorOrHost && (
                                                <span
                                                    className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                    style={{ opacity: 0.45, cursor: 'default' }}
                                                >
                                                    Refer a Creator (Coming Soon)
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        }

                        if (item.label === 'Hosts') {
                            return (
                                <div key={index}>
                                    <button
                                        className={styles.mobileNavItem}
                                        onClick={() => setMobileHostsExpanded(!mobileHostsExpanded)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            width: '100%',
                                            textAlign: 'left',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        Hosts
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 16 16"
                                            fill="currentColor"
                                            style={{
                                                transition: 'transform 0.3s ease',
                                                transform: mobileHostsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            }}
                                        >
                                            <path d="M10.805 6.362c-.26-.26-.682-.26-.943 0L8 8.224 6.138 6.362c-.26-.26-.682-.26-.943 0s-.26.682 0 .943l2.333 2.333c.26.26.682.26.943 0l2.333-2.333c.26-.26.26-.682 0-.943z" />
                                        </svg>
                                    </button>
                                    {mobileHostsExpanded && (
                                        <>
                                            <Link
                                                to="/hosts"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Search Our Properties
                                            </Link>
                                            <Link
                                                to="/hosts/storefronts"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Shop Our Properties
                                            </Link>
                                            <Link
                                                to="/hosts/spotlight"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem, styles.mobileSubItemWithBadge)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                <span>Host Spotlight</span>
                                                <span className={styles.newBadge}>New</span>
                                            </Link>
                                            {isCreatorOrHost && (
                                                <Link
                                                    to="/hosts/reviews"
                                                    className={cn(styles.mobileNavItem, styles.mobileSubItem, styles.mobileSubItemWithBadge)}
                                                    onClick={() => setMobileMenuOpen(false)}
                                                >
                                                    <span>Reviews</span>
                                                    <span className={styles.newBadge}>New</span>
                                                </Link>
                                            )}
                                            {isCreatorOrHost && (
                                                <Link
                                                    to="/hosts/get-featured"
                                                    className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                    onClick={() => setMobileMenuOpen(false)}
                                                >
                                                    Get Featured
                                                </Link>
                                            )}
                                            {isCreatorOrHost && (
                                                <Link
                                                    to="/get-verified"
                                                    className={cn(styles.mobileNavItem, styles.mobileSubItem, styles.mobileSubItemWithBadge)}
                                                    onClick={() => setMobileMenuOpen(false)}
                                                >
                                                    <span>Get Verified</span>
                                                    <span className={styles.newBadge}>New</span>
                                                </Link>
                                            )}
                                            <Link
                                                to="/hosts/resources"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem, styles.mobileSubItemWithBadge)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                <span>Host Resources</span>
                                                <span className={styles.newBadge}>New</span>
                                            </Link>
                                            {isCreatorOrHost && (
                                                <span
                                                    className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                    style={{ opacity: 0.45, cursor: 'default' }}
                                                >
                                                    Refer a Host (Coming Soon)
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        }

                        if (item.label === 'Collabs') {
                            return (
                                <div key={index}>
                                    <button
                                        className={styles.mobileNavItem}
                                        onClick={() => setMobileCollabExpanded(!mobileCollabExpanded)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            width: '100%',
                                            textAlign: 'left',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        Collabs
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 16 16"
                                            fill="currentColor"
                                            style={{
                                                transition: 'transform 0.3s ease',
                                                transform: mobileCollabExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            }}
                                        >
                                            <path d="M10.805 6.362c-.26-.26-.682-.26-.943 0L8 8.224 6.138 6.362c-.26-.26-.682-.26-.943 0s-.26.682 0 .943l2.333 2.333c.26.26.682.26.943 0l2.333-2.333c.26-.26.26-.682 0-.943z" />
                                        </svg>
                                    </button>
                                    {mobileCollabExpanded && (
                                        <>
                                            <Link
                                                to="/collab-board"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Current Opportunities
                                            </Link>
                                            <Link
                                                to="/collabs"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                My Collab Tracker
                                            </Link>
                                            <span
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                style={{ opacity: 0.45, cursor: 'default' }}
                                            >
                                                Best Practices (Coming Soon)
                                            </span>
                                            <span
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                style={{ opacity: 0.45, cursor: 'default' }}
                                            >
                                                FAQ (Coming Soon)
                                            </span>
                                            <span
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                style={{ opacity: 0.45, cursor: 'default' }}
                                            >
                                                Postaway Collabs (Coming Soon)
                                            </span>
                                        </>
                                    )}
                                </div>
                            );
                        }

                        if (item.label === 'Travel') {
                            return (
                                <div key={index}>
                                    <button
                                        className={styles.mobileNavItem}
                                        onClick={() => setMobileTravelExpanded(!mobileTravelExpanded)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            width: '100%',
                                            textAlign: 'left',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        Travel
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 16 16"
                                            fill="currentColor"
                                            style={{
                                                transition: 'transform 0.3s ease',
                                                transform: mobileTravelExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            }}
                                        >
                                            <path d="M10.805 6.362c-.26-.26-.682-.26-.943 0L8 8.224 6.138 6.362c-.26-.26-.682-.26-.943 0s-.26.682 0 .943l2.333 2.333c.26.26.682.26.943 0l2.333-2.333c.26-.26.26-.682 0-.943z" />
                                        </svg>
                                    </button>
                                    {mobileTravelExpanded && (
                                        <>
                                            <Link
                                                to="/travel-board"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                All Opportunities
                                            </Link>
                                            <Link
                                                to="/itineraries"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Itineraries
                                            </Link>
                                            <Link
                                                to="/travel-board?filter=group_trips"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Group Trips
                                            </Link>
                                            <Link
                                                to="/travel-board?filter=substack"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Substack / Newsletter / Blog
                                            </Link>
                                            <Link
                                                to="/travel-board?filter=podcast"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Podcast
                                            </Link>
                                            <Link
                                                to="/travel-board?filter=last_minute"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Last Minute Availability
                                            </Link>
                                            <Link
                                                to="/travel-board?filter=discount_codes"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Discount Codes
                                            </Link>
                                            <Link
                                                to="/travel-board?filter=upcoming_events"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Upcoming Events
                                            </Link>
                                            <Link
                                                to="/travel-board"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                General Announcement
                                            </Link>
                                        </>
                                    )}
                                </div>
                            );
                        }

                        if (item.label === 'Partners') {
                            return (
                                <div key={index}>
                                    <button
                                        className={styles.mobileNavItem}
                                        onClick={() => setMobilePartnersExpanded(!mobilePartnersExpanded)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            width: '100%',
                                            textAlign: 'left',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        Partners
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 16 16"
                                            fill="currentColor"
                                            style={{
                                                transition: 'transform 0.3s ease',
                                                transform: mobilePartnersExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            }}
                                        >
                                            <path d="M10.805 6.362c-.26-.26-.682-.26-.943 0L8 8.224 6.138 6.362c-.26-.26-.682-.26-.943 0s-.26.682 0 .943l2.333 2.333c.26.26.682.26.943 0l2.333-2.333c.26-.26.26-.682 0-.943z" />
                                        </svg>
                                    </button>
                                    {mobilePartnersExpanded && (
                                        <>
                                            <Link
                                                to="/partners"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Preferred Partners
                                            </Link>
                                            {PARTNER_CATEGORIES.map((label) => (
                                                <Link
                                                    key={label}
                                                    to={`/partners?category=${encodeURIComponent(label)}`}
                                                    className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                    onClick={() => setMobileMenuOpen(false)}
                                                >
                                                    {label}
                                                </Link>
                                            ))}
                                            <Link
                                                to="/partners"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Apply to be a Partner
                                            </Link>
                                        </>
                                    )}
                                </div>
                            );
                        }

                        if (item.label === 'Events') {
                            return (
                                <div key={index}>
                                    <button
                                        className={styles.mobileNavItem}
                                        onClick={() => setMobileEventsExpanded(!mobileEventsExpanded)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            width: '100%',
                                            textAlign: 'left',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        Events
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 16 16"
                                            fill="currentColor"
                                            style={{
                                                transition: 'transform 0.3s ease',
                                                transform: mobileEventsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            }}
                                        >
                                            <path d="M10.805 6.362c-.26-.26-.682-.26-.943 0L8 8.224 6.138 6.362c-.26-.26-.682-.26-.943 0s-.26.682 0 .943l2.333 2.333c.26.26.682.26.943 0l2.333-2.333c.26-.26.26-.682 0-.943z" />
                                        </svg>
                                    </button>
                                    {mobileEventsExpanded && (
                                        <>
                                            <Link
                                                to="/hospitality-summit-2027"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Hospitality Summit 2027
                                            </Link>
                                            <Link
                                                to="/hospitality-summit"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Hospitality Summit 2026
                                            </Link>
                                            <Link
                                                to="/inner-circle"
                                                className={cn(styles.mobileNavItem, styles.mobileSubItem)}
                                                onClick={() => setMobileMenuOpen(false)}
                                            >
                                                Inner Circle Program
                                            </Link>
                                        </>
                                    )}
                                </div>
                            );
                        }

                        return item.external ? (
                            <a
                                key={index}
                                href={item.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.mobileNavItem}
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {item.label}
                            </a>
                        ) : (
                            <Link
                                key={index}
                                to={item.path}
                                className={styles.mobileNavItem}
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                {item.label}
                            </Link>
                        );
                    })}
                    {canSeeNav && !isTraveler && !isPartner && (
                        <Link
                            to="/profile"
                            className={styles.mobileNavItem}
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <svg 
                                    width="20" 
                                    height="20" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{ color: 'currentColor' }}
                                >
                                    <path 
                                        d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" 
                                        fill="currentColor"
                                    />
                                    <path 
                                        d="M12 14C7.03125 14 3 18.0312 3 23H21C21 18.0312 16.9688 14 12 14Z" 
                                        fill="currentColor"
                                    />
                                </svg>
                                Profile
                            </span>
                        </Link>
                    )}
                    {canSeeNav && isTraveler && (
                        <>
                            <Link
                                to="/traveler-settings"
                                className={styles.mobileNavItem}
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ fill: 'none' }}>
                                        <path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915" fill="none" style={{ fill: 'none' }} />
                                        <circle cx="12" cy="12" r="3" fill="none" style={{ fill: 'none' }} />
                                    </svg>
                                    Settings
                                </span>
                            </Link>
                            <button
                                className={styles.mobileNavItem}
                                onClick={() => {
                                    setMobileMenuOpen(false);
                                    aggressiveLogout(disconnectChat || null);
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    width: '100%',
                                    textAlign: 'left',
                                    color: '#DC1E07'
                                }}
                            >
                                Logout
                            </button>
                        </>
                    )}
                    {canSeeNav && isPartner && (
                        <>
                            <Link
                                to="/messages"
                                className={styles.mobileNavItem}
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <Icon name="message" size="20" />
                                    Messages
                                </span>
                            </Link>
                            <Link
                                to="/partner-settings"
                                className={styles.mobileNavItem}
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ fill: 'none' }}>
                                        <path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915" fill="none" style={{ fill: 'none' }} />
                                        <circle cx="12" cy="12" r="3" fill="none" style={{ fill: 'none' }} />
                                    </svg>
                                    Partner Settings
                                </span>
                            </Link>
                            <button
                                className={styles.mobileNavItem}
                                onClick={() => {
                                    setMobileMenuOpen(false);
                                    aggressiveLogout(disconnectChat || null);
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    width: '100%',
                                    textAlign: 'left',
                                    color: '#DC1E07'
                                }}
                            >
                                Logout
                            </button>
                        </>
                    )}
                </nav>
            </div>
        </>
    );
};

export default Header;
